import { useEffect, useSyncExternalStore } from 'react';

/** The part of the page the user can actually see, in pixels. */
export interface VisibleArea {
  top: number;
  height: number;
  /** The on-screen keyboard is taking part of the window. */
  keyboard: boolean;
}

/* Less than this missing from the bottom is browser chrome moving, not a keyboard. */
const KEYBOARD_MIN_PX = 120;

/* The last answer, handed out again while nothing changed: a store snapshot must be stable. */
let lastArea: VisibleArea | null = null;

function visibleArea(): VisibleArea {
  const { visualViewport } = window;
  const top = visualViewport?.offsetTop ?? 0;
  const height = visualViewport?.height ?? window.innerHeight;
  const keyboard = document.documentElement.clientHeight - height > KEYBOARD_MIN_PX;

  if (
    lastArea?.top !== top ||
    lastArea.height !== height ||
    lastArea.keyboard !== keyboard
  ) {
    lastArea = { top, height, keyboard };
  }

  return lastArea;
}

function subscribe(onChange: () => void): () => void {
  window.visualViewport?.addEventListener('resize', onChange);
  window.visualViewport?.addEventListener('scroll', onChange);
  window.addEventListener('resize', onChange);

  return () => {
    window.visualViewport?.removeEventListener('resize', onChange);
    window.visualViewport?.removeEventListener('scroll', onChange);
    window.removeEventListener('resize', onChange);
  };
}

function noSubscription(): () => void {
  return () => {};
}

/**
 * What the reader can see while `active`, or null when not active. Viewport
 * units cannot follow the iOS keyboard: it shrinks only the visual viewport and
 * pans the page under a fixed element instead. The page behind is held still
 * meanwhile, so the only thing that scrolls is the surface.
 */
export function useVisibleArea(active: boolean): VisibleArea | null {
  const area = useSyncExternalStore(
    active ? subscribe : noSubscription,
    () => (active ? visibleArea() : null),
    () => null,
  );

  useEffect(() => {
    if (!active) return;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [active]);

  return area;
}
